import { useState, useEffect, useRef, useCallback } from 'react';
import Iconify from 'src/components/Iconify';
import useAuth from 'src/hooks/useAuth';
import { MKT_ANALYTICS_DESIGN as d } from './MktAnalytics.design';
import { exchangeMetaCode, connectMetaAccount } from 'src/actions/v2/marketing/analytics/v2_actionMktAccounts';

const MKT_OAUTH_CHANNEL = 'mkt_oauth_channel';
const MKT_OAUTH_STORAGE_KEY = 'mkt_oauth_code';

const FACEBOOK_APP_ID = import.meta.env.VITE_FACEBOOK_APP_ID || '2587072681672869';

const META_SCOPES = [
    'pages_show_list',
    'pages_read_engagement',
    'pages_read_user_content',
    'instagram_basic',
    'instagram_manage_insights',
    'read_insights'
].join(',');

const PLATFORMS = [
    { id: 'meta', label: 'Instagram + Facebook', icon: 'mdi:facebook', color: 'bg-blue-50 border-blue-200 text-blue-700', activeColor: 'bg-blue-100 border-blue-400 ring-2 ring-blue-300', description: 'Conectar via Meta (páginas do Facebook e contas do Instagram vinculadas)' },
    { id: 'google_ads', label: 'Google Ads', icon: 'mdi:google-ads', color: 'bg-amber-50 border-amber-200 text-amber-700', activeColor: 'bg-amber-100 border-amber-400 ring-2 ring-amber-300', description: 'Em breve', disabled: true }
];

// Steps: 'select_platform' → 'authenticating' → 'select_accounts' → 'connecting' → 'done'

export default function MktAccountDrawer({ open, onClose, onRefresh }) {
    const { user } = useAuth();
    const [step, setStep] = useState('select_platform');
    const [error, setError] = useState('');
    const [discoveredAccounts, setDiscoveredAccounts] = useState([]);
    const [selectedAccounts, setSelectedAccounts] = useState([]);
    const [oauthData, setOauthData] = useState(null);
    const [loading, setLoading] = useState(false);

    const popupRef = useRef(null);
    const pollingRef = useRef(null);

    const resetState = useCallback(() => {
        setStep('select_platform');
        setError('');
        setDiscoveredAccounts([]);
        setSelectedAccounts([]);
        setOauthData(null);
        setLoading(false);
    }, []);

    const handleClose = () => {
        stopPolling();
        resetState();
        onClose();
    };

    const stopPolling = useCallback(() => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    }, []);

    // Handle code received from popup
    const handleCodeReceived = useCallback(async (code) => {
        stopPolling();
        setStep('authenticating');
        setLoading(true);
        setError('');

        try {
            const redirectUri = window.location.href.split('?')[0];
            const response = await exchangeMetaCode(user.clienteId, { code, redirect_uri: redirectUri });

            const { userAccessToken, expiresIn, accounts } = response.data.data;

            if (!accounts || accounts.length === 0) {
                setError('Nenhuma página ou conta do Instagram encontrada. Verifique se sua conta do Facebook tem páginas vinculadas.');
                setStep('select_platform');
                setLoading(false);
                return;
            }

            setOauthData({ userAccessToken, expiresIn });
            setDiscoveredAccounts(accounts);
            setSelectedAccounts(accounts.map((_, i) => i)); // Select all by default
            setStep('select_accounts');
        } catch (err) {
            console.error('[MKT OAuth] Exchange failed:', err);
            const msg = err.response?.data?.details?.message || err.response?.data?.message || 'Erro ao autenticar com o Facebook';
            setError(msg);
            setStep('select_platform');
        } finally {
            setLoading(false);
        }
    }, [user?.clienteId, stopPolling]);

    // Listen for code from popup
    useEffect(() => {
        if (!open) return;

        // BroadcastChannel listener
        let bc;
        try {
            bc = new BroadcastChannel(MKT_OAUTH_CHANNEL);
            bc.onmessage = (event) => {
                if (event.data?.type === 'MKT_OAUTH_CODE' && event.data.code) {
                    handleCodeReceived(event.data.code);
                }
            };
        } catch (_) { /* not supported */ }

        // postMessage listener
        const handleMessage = (event) => {
            if (event.origin === window.location.origin && event.data?.type === 'MKT_OAUTH_CODE' && event.data.code) {
                handleCodeReceived(event.data.code);
            }
        };
        window.addEventListener('message', handleMessage);

        // localStorage listener
        const handleStorage = (event) => {
            if (event.key === MKT_OAUTH_STORAGE_KEY && event.newValue) {
                handleCodeReceived(event.newValue);
                localStorage.removeItem(MKT_OAUTH_STORAGE_KEY);
            }
        };
        window.addEventListener('storage', handleStorage);

        return () => {
            if (bc) bc.close();
            window.removeEventListener('message', handleMessage);
            window.removeEventListener('storage', handleStorage);
        };
    }, [open, handleCodeReceived]);

    // Check if current page has ?code= param (we are the popup)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        if (code && open) {
            // We received the code directly (redirect, not popup)
            handleCodeReceived(code);
            // Clean URL
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, [open, handleCodeReceived]);

    const openMetaOAuth = () => {
        setError('');
        setStep('authenticating');

        const redirectUri = window.location.href.split('?')[0];
        const oauthUrl = `https://www.facebook.com/v22.0/dialog/oauth?client_id=${FACEBOOK_APP_ID}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${META_SCOPES}&response_type=code`;

        // Open popup
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.innerWidth - width) / 2;
        const top = window.screenY + (window.innerHeight - height) / 2;

        popupRef.current = window.open(
            oauthUrl,
            'mkt_meta_oauth',
            `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
        );

        // Poll for localStorage fallback
        localStorage.removeItem(MKT_OAUTH_STORAGE_KEY);
        pollingRef.current = setInterval(() => {
            const code = localStorage.getItem(MKT_OAUTH_STORAGE_KEY);
            if (code) {
                localStorage.removeItem(MKT_OAUTH_STORAGE_KEY);
                handleCodeReceived(code);
            }

            // Check if popup was closed without completing
            if (popupRef.current && popupRef.current.closed) {
                stopPolling();
                if (step === 'authenticating') {
                    setStep('select_platform');
                }
            }
        }, 1000);
    };

    const toggleAccount = (index) => {
        setSelectedAccounts(prev =>
            prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
        );
    };

    const handleConnect = async () => {
        if (selectedAccounts.length === 0) return;
        setLoading(true);
        setError('');

        try {
            const accountsToConnect = selectedAccounts.map(i => discoveredAccounts[i]);
            await connectMetaAccount(user.clienteId, {
                user_id: user.id,
                userAccessToken: oauthData.userAccessToken,
                expiresIn: oauthData.expiresIn,
                accounts: accountsToConnect
            });

            setStep('done');
            if (onRefresh) onRefresh();
        } catch (err) {
            console.error('[MKT OAuth] Connect failed:', err);
            setError(err.response?.data?.message || 'Erro ao salvar contas');
        } finally {
            setLoading(false);
        }
    };

    if (!open) return null;

    return (
        <div className={d.drawer.overlay}>
            <div className={d.drawer.backdrop} onClick={handleClose} />
            <div className={d.drawer.panel}>
                {/* Header */}
                <div className={d.drawer.header}>
                    <div className="flex items-center gap-3">
                        <div className={d.drawer.headerIcon}>
                            <Iconify icon="eva:link-2-outline" width={20} className="text-violet-600" />
                        </div>
                        <div>
                            <h2 className={d.drawer.headerTitle}>Conectar Conta</h2>
                            <p className={d.drawer.headerSubtitle}>
                                {step === 'select_platform' && 'Selecione a plataforma para conectar'}
                                {step === 'authenticating' && 'Autenticando com o Facebook...'}
                                {step === 'select_accounts' && 'Selecione as contas para conectar'}
                                {step === 'connecting' && 'Conectando contas...'}
                                {step === 'done' && 'Contas conectadas com sucesso!'}
                            </p>
                        </div>
                    </div>
                    <button className={d.drawer.closeBtn} onClick={handleClose}>
                        <Iconify icon="eva:close-outline" width={20} className="text-gray-500" />
                    </button>
                </div>

                {/* Error Banner */}
                {error && (
                    <div className="px-6 py-3 bg-red-50 border-b border-red-100 flex items-center gap-2">
                        <Iconify icon="eva:alert-circle-outline" width={18} className="text-red-600 flex-shrink-0" />
                        <p className="text-sm text-red-700">{error}</p>
                    </div>
                )}

                {/* Body */}
                <div className={d.drawer.body}>

                    {/* Step 1: Select Platform */}
                    {step === 'select_platform' && (
                        <div className="space-y-4">
                            {PLATFORMS.map((platform) => (
                                <button
                                    key={platform.id}
                                    className={`w-full flex items-center gap-4 p-5 rounded-xl border transition-all ${
                                        platform.disabled
                                            ? 'bg-gray-50 border-gray-200 opacity-60 cursor-not-allowed'
                                            : `${platform.color} hover:shadow-md cursor-pointer`
                                    }`}
                                    onClick={() => {
                                        if (platform.disabled) return;
                                        if (platform.id === 'meta') openMetaOAuth();
                                    }}
                                    disabled={platform.disabled}
                                >
                                    <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-white/80">
                                        <Iconify icon={platform.icon} width={28} />
                                    </div>
                                    <div className="text-left flex-1">
                                        <p className="text-sm font-semibold">{platform.label}</p>
                                        <p className="text-xs opacity-70">{platform.description}</p>
                                    </div>
                                    {!platform.disabled && (
                                        <Iconify icon="eva:arrow-forward-outline" width={20} />
                                    )}
                                    {platform.disabled && (
                                        <span className="text-[10px] font-medium bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">Em breve</span>
                                    )}
                                </button>
                            ))}

                            <div className="bg-blue-50/50 rounded-xl border border-blue-100 p-4 flex items-start gap-3 mt-6">
                                <Iconify icon="eva:info-outline" width={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
                                <div>
                                    <p className="text-sm font-medium text-blue-900 mb-1">Como funciona</p>
                                    <p className="text-xs text-blue-700">
                                        Ao clicar em "Instagram + Facebook", uma janela do Facebook será aberta para autorizar o acesso.
                                        Você poderá selecionar quais páginas e contas do Instagram deseja conectar.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Authenticating */}
                    {step === 'authenticating' && (
                        <div className="flex flex-col items-center justify-center py-16">
                            <div className="w-12 h-12 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-4" />
                            <p className="text-sm font-medium text-gray-700 mb-1">Aguardando autorização do Facebook</p>
                            <p className="text-xs text-gray-500 text-center max-w-sm">
                                Complete o login na janela do Facebook que foi aberta.
                                Se a janela não apareceu, verifique se seu navegador bloqueou o popup.
                            </p>
                            <button
                                className="mt-4 text-xs text-[#7C3AED] hover:text-[#6d28d9] font-medium cursor-pointer"
                                onClick={() => { stopPolling(); setStep('select_platform'); }}
                            >
                                Cancelar
                            </button>
                        </div>
                    )}

                    {/* Step 3: Select Accounts */}
                    {step === 'select_accounts' && (
                        <div className="space-y-3">
                            <p className="text-sm text-gray-600 mb-4">
                                Encontramos {discoveredAccounts.length} conta(s). Selecione quais deseja conectar:
                            </p>

                            {discoveredAccounts.map((acc, index) => {
                                const isSelected = selectedAccounts.includes(index);
                                const isIG = acc.provider === 'instagram';
                                return (
                                    <button
                                        key={`${acc.provider}-${acc.account_id}`}
                                        className={`w-full flex items-center gap-3 p-4 rounded-xl border transition-all cursor-pointer ${
                                            isSelected
                                                ? 'bg-violet-50 border-violet-300 ring-2 ring-violet-200'
                                                : 'bg-white border-gray-200 hover:border-gray-300'
                                        }`}
                                        onClick={() => toggleAccount(index)}
                                    >
                                        {/* Checkbox */}
                                        <div className={`w-5 h-5 rounded flex items-center justify-center border-2 flex-shrink-0 ${
                                            isSelected ? 'bg-[#7C3AED] border-[#7C3AED]' : 'border-gray-300'
                                        }`}>
                                            {isSelected && <Iconify icon="eva:checkmark-fill" width={14} className="text-white" />}
                                        </div>

                                        {/* Avatar */}
                                        {acc.profile_picture ? (
                                            <img src={acc.profile_picture} alt="" className="w-10 h-10 rounded-xl object-cover flex-shrink-0" />
                                        ) : (
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isIG ? 'bg-pink-100' : 'bg-blue-100'}`}>
                                                <Iconify icon={isIG ? 'mdi:instagram' : 'mdi:facebook'} width={20} className={isIG ? 'text-pink-600' : 'text-blue-600'} />
                                            </div>
                                        )}

                                        {/* Info */}
                                        <div className="text-left flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-gray-900 truncate">{acc.account_name}</p>
                                            <p className="text-xs text-gray-500 truncate">
                                                {isIG && acc.account_info?.username && `@${acc.account_info.username}`}
                                                {!isIG && acc.account_info?.category}
                                            </p>
                                        </div>

                                        {/* Badge */}
                                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                                            isIG ? 'bg-pink-50 text-pink-700 border border-pink-200' : 'bg-blue-50 text-blue-700 border border-blue-200'
                                        }`}>
                                            {isIG ? 'Instagram' : 'Facebook'}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* Step 4: Done */}
                    {step === 'done' && (
                        <div className="flex flex-col items-center justify-center py-16">
                            <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-green-100 mb-4">
                                <Iconify icon="eva:checkmark-circle-2-fill" width={40} className="text-green-600" />
                            </div>
                            <p className="text-lg font-semibold text-gray-900 mb-1">Contas conectadas!</p>
                            <p className="text-sm text-gray-500 text-center max-w-sm">
                                Suas contas foram vinculadas com sucesso. Os dados de analytics serão atualizados automaticamente.
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className={d.drawer.footer}>
                    {step === 'select_accounts' && (
                        <>
                            <button className={d.drawer.cancelBtn} onClick={() => setStep('select_platform')}>
                                Voltar
                            </button>
                            <button
                                className={`${d.drawer.saveBtn} ${selectedAccounts.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                                onClick={handleConnect}
                                disabled={selectedAccounts.length === 0 || loading}
                            >
                                {loading ? (
                                    <><Iconify icon="eva:loader-outline" width={16} className="animate-spin inline mr-1" /> Conectando...</>
                                ) : (
                                    <><Iconify icon="eva:link-2-outline" width={16} className="inline mr-1" /> Conectar {selectedAccounts.length} conta(s)</>
                                )}
                            </button>
                        </>
                    )}

                    {step === 'done' && (
                        <button className={d.drawer.saveBtn} onClick={handleClose}>
                            Fechar
                        </button>
                    )}

                    {(step === 'select_platform' || step === 'authenticating') && (
                        <button className={d.drawer.cancelBtn} onClick={handleClose}>
                            Cancelar
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
